import React, { useCallback } from 'react'

import type { List as ImmutableList } from 'immutable'

import { queueTrackingEvent } from '@library/tracking-integration'

import DropdownInput from 'components/Form/Dropdown'
import type { UserOrder } from 'utils/types/order'

import { Calendar } from '../Calendar'
import { SlotPicker } from '../SlotPicker'
import type { Slots } from '../types'

import css from '../DeliverySlot.css'

type DeliveryDay = {
  date: string
  value: string
  disabled: boolean
  orderId?: string | null
  icon: string | null
}

type DatePickerProps = {
  slots: Slots
  slotId: string
  deliveryDays: DeliveryDay[]
  disableNewDatePicker: boolean
  disabled?: boolean
  tempDate: string
  tempSlotId: string
  tempOrderId: string | undefined
  setTempSlotId: (slotId: string) => void
  handleDateChange: (date: string, orderId: string) => void
  subLabelClassName: string
  hideSlotPicker: boolean
  userOrders?: ImmutableList<UserOrder>
}

export const DatePicker = (props: DatePickerProps) => {
  const {
    slots,
    slotId,
    deliveryDays,
    disableNewDatePicker,
    disabled = false,
    tempDate,
    tempSlotId,
    tempOrderId,
    setTempSlotId,
    handleDateChange,
    subLabelClassName,
    hideSlotPicker = false,
    userOrders,
  } = props
  const deliverySlotOptions = slots[tempDate] || []

  // Blocks date/slot selection while an address reload is in flight (disabled=true) - otherwise a
  // manual selection made during that window gets silently discarded once the reload resolves and
  // re-pins the date/slot that was captured before the address change was confirmed.
  const enrichedHandleDateChange = useCallback(
    (date, orderId) => {
      if (disabled) {
        return
      }

      handleDateChange(date, orderId)
      queueTrackingEvent<{
        action: 'choose_delivery_date'
      }>({
        action: 'choose_delivery_date',
        properties: {
          delivery_date: date,
        },
      })
    },
    [disabled, handleDateChange],
  )

  const guardedSetTempSlotId = useCallback(
    (newSlotId: string) => {
      if (disabled) {
        return
      }

      setTempSlotId(newSlotId)
    },
    [disabled, setTempSlotId],
  )

  return disableNewDatePicker ? (
    <div className={css.bsRow}>
      <div className={css.halfLeft}>
        <DropdownInput
          color="secondary"
          uppercase
          options={deliveryDays}
          onChange={enrichedHandleDateChange}
          value={tempDate}
          className={css.dropdown}
          subLabelClassName={subLabelClassName}
        />
      </div>
      <div className={css.halfRight}>
        <DropdownInput
          color="secondary"
          uppercase
          options={deliverySlotOptions}
          onChange={guardedSetTempSlotId}
          value={tempSlotId}
          className={css.dropdown}
        />
      </div>
    </div>
  ) : (
    <span>
      <div className={css.row}>
        <Calendar
          dates={deliveryDays}
          selected={tempDate}
          onClick={enrichedHandleDateChange}
          userOrders={userOrders}
        />
      </div>
      {!hideSlotPicker && (
        <div className={tempOrderId ? css.disabledRow : css.row}>
          <SlotPicker
            slots={slots}
            date={tempDate}
            slotId={slotId}
            onClick={guardedSetTempSlotId}
          />
        </div>
      )}
    </span>
  )
}
